import type { ReportColumn } from './ReportDataTable'
import type { KpiMetricBreakdownRow, ReportFilters } from '@/lib/services/helpdeskReportService'
import { METRIC_TABLE_COLUMNS, type MetricRow } from './reportTableColumns'

export type DetailRow = Record<string, string | number | boolean>

export const KPI_BREAKDOWN_COLUMNS: ReportColumn<KpiMetricBreakdownRow>[] = [
  { key: 'metric', header: 'Metric' },
  { key: 'formula', header: 'Formula', maxW: '280px', isTruncated: true },
  { key: 'met', header: 'Met / On-time' },
  { key: 'total', header: 'Total' },
  {
    key: 'threshold',
    header: 'Threshold',
    render: (row) => row.threshold ?? '—',
  },
  { key: 'result', header: 'Result' },
]

export const SD_ACK_DETAIL_COLUMNS: ReportColumn<DetailRow>[] = [
  { key: 'reference', header: 'Reference' },
  {
    key: 'submitted_at',
    header: 'Submitted',
    render: (row) => formatDateTime(row.submitted_at),
  },
  {
    key: 'acknowledged_at',
    header: 'Acknowledged',
    render: (row) => formatDateTime(row.acknowledged_at),
  },
  { key: 'delay_minutes', header: 'Delay (min)' },
  { key: 'met', header: 'Met' },
]

export const SD_CLOSING_DETAIL_COLUMNS: ReportColumn<DetailRow>[] = [
  { key: 'ticket_number', header: 'Ticket #' },
  {
    key: 'created_at',
    header: 'Created',
    render: (row) => formatDateTime(row.created_at),
  },
  {
    key: 'closed_at',
    header: 'Closed',
    render: (row) => formatDateTime(row.closed_at),
  },
  { key: 'duration_minutes', header: 'Duration (min)' },
  { key: 'met', header: 'Met' },
]

export const SD_CREATION_DETAIL_COLUMNS: ReportColumn<DetailRow>[] = [
  { key: 'ticket_number', header: 'Ticket #' },
  {
    key: 'acknowledged_at',
    header: 'Acknowledged',
    render: (row) => formatDateTime(row.acknowledged_at),
  },
  {
    key: 'first_child_at',
    header: 'First child',
    render: (row) => formatDateTime(row.first_child_at),
  },
  { key: 'hours_elapsed', header: 'Hours' },
  { key: 'met', header: 'Met' },
]

export const FE_TASK_DETAIL_COLUMNS: ReportColumn<DetailRow>[] = [
  { key: 'task_number', header: 'Task #' },
  { key: 'parent_ticket_number', header: 'Parent ticket' },
  {
    key: 'created_at',
    header: 'Created',
    render: (row) => formatDateTime(row.created_at),
  },
  {
    key: 'actual_start',
    header: 'Actual start',
    render: (row) => formatDateTime(row.actual_start),
  },
  {
    key: 'actual_end',
    header: 'Actual end',
    render: (row) => formatDateTime(row.actual_end),
  },
  {
    key: 'scheduled_start',
    header: 'Sched. start',
    render: (row) => formatDateTime(row.scheduled_start),
  },
  {
    key: 'scheduled_end',
    header: 'Sched. end',
    render: (row) => formatDateTime(row.scheduled_end),
  },
  { key: 'acceptance_met', header: 'Accept met' },
  { key: 'resolution_met', header: 'Resolve met' },
  { key: 'status', header: 'Status' },
]

export const APR_ACK_DETAIL_COLUMNS: ReportColumn<DetailRow>[] = [
  { key: 'reference', header: 'Reference' },
  {
    key: 'submitted_at',
    header: 'Submitted',
    render: (row) => formatDateTime(row.submitted_at),
  },
  {
    key: 'acknowledged_at',
    header: 'Acknowledged',
    render: (row) => formatDateTime(row.acknowledged_at),
  },
  { key: 'minutes', header: 'Minutes' },
]

export const APR_PARENT_CHILD_DETAIL_COLUMNS: ReportColumn<DetailRow>[] = [
  { key: 'ticket_number', header: 'Ticket #' },
  {
    key: 'parent_created_at',
    header: 'Parent created',
    render: (row) => formatDateTime(row.parent_created_at),
  },
  {
    key: 'first_child_at',
    header: 'First child',
    render: (row) => formatDateTime(row.first_child_at),
  },
  { key: 'minutes', header: 'Minutes' },
]

export const APR_CHILD_CLOSE_DETAIL_COLUMNS: ReportColumn<DetailRow>[] = [
  { key: 'ticket_number', header: 'Ticket #' },
  {
    key: 'last_child_end',
    header: 'Last child end',
    render: (row) => formatDateTime(row.last_child_end),
  },
  {
    key: 'closed_at',
    header: 'Parent closed',
    render: (row) => formatDateTime(row.closed_at),
  },
  { key: 'minutes', header: 'Minutes' },
]

export { METRIC_TABLE_COLUMNS }

export function formatFilterRange(filters: ReportFilters): string {
  if (filters.from && filters.to) return `${filters.from} – ${filters.to}`
  if (filters.from) return `From ${filters.from}`
  if (filters.to) return `Until ${filters.to}`
  return 'All dates (no filter applied)'
}

export function formatDateTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  const d = new Date(String(value))
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString()
}

export function buildSdSummaryRows(summary: Record<string, number> | undefined): MetricRow[] {
  const s = summary ?? {}
  return [
    { metric: 'Acknowledgement rate', value: `${s.acknowledgement_rate_percent ?? 0}%` },
    { metric: 'On-time closing', value: `${s.on_time_closing_percent ?? 0}%` },
    { metric: 'On-time creation', value: `${s.on_time_creation_percent ?? 0}%` },
    { metric: 'Closing rate', value: `${s.closing_rate_percent ?? 0}%` },
    { metric: 'Ack on-time / total', value: `${s.acknowledgement_on_time ?? 0} / ${s.acknowledgement_total ?? 0}` },
    { metric: 'Closed on-time / total', value: `${s.on_time_closing_count ?? 0} / ${s.closed_total ?? 0}` },
    { metric: 'Creation on-time / eligible', value: `${s.on_time_creation_count ?? 0} / ${s.creation_eligible_total ?? 0}` },
  ]
}

export function buildFeSummaryRows(summary: Record<string, number> | undefined): MetricRow[] {
  const s = summary ?? {}
  return [
    { metric: 'Acceptance rate', value: `${s.acceptance_rate_percent ?? 0}%` },
    { metric: 'On-time acceptance', value: `${s.on_time_acceptance_percent ?? 0}%` },
    { metric: 'Resolution rate', value: `${s.resolution_rate_percent ?? 0}%` },
    { metric: 'On-time resolution', value: `${s.on_time_resolution_percent ?? 0}%` },
    { metric: 'Response (arrival proxy)', value: `${s.response_rate_percent ?? 0}%` },
    { metric: 'Rejection rate', value: `${s.rejection_rate_percent ?? 0}%` },
    { metric: 'APT Breakfix (min)', value: s.apt_breakfix_minutes ?? 0 },
    { metric: 'APT Adhoc (min)', value: s.apt_adhoc_minutes ?? 0 },
    { metric: 'Total tasks', value: s.total_tasks ?? 0 },
  ]
}

export function buildAprSummaryRows(summary: Record<string, number> | undefined): MetricRow[] {
  const s = summary ?? {}
  return [
    { metric: 'Ack duration avg (min)', value: s.acknowledgement_duration_avg_minutes ?? 0 },
    { metric: 'Parent → first child (min)', value: s.parent_to_first_child_avg_minutes ?? 0 },
    { metric: 'Last child → parent close (min)', value: s.last_child_to_parent_close_avg_minutes ?? 0 },
  ]
}

export function formatThresholds(thresholds: Record<string, number> | undefined): string | null {
  if (!thresholds || Object.keys(thresholds).length === 0) return null
  return Object.entries(thresholds)
    .map(([key, value]) => {
      const label = key.replace(/_/g, ' ')
      const unit = key.includes('hours') ? 'hr' : 'min'
      return `${label}: ${value} ${unit}`
    })
    .join(' · ')
}
